import { AfterViewInit, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { disabled, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'

import { Subject, of } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { AnnaKayttajanTiedotReq, LuoTiliReq, LuoTiliResp, MagicLinkErrorResponse, TervetuloaAnnaKayttajanTiedotResp } from '@lemon/core-firebase/model/magic-link'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { LoginInputData, LoginService } from '../_angular/service/login.service'
import { asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

interface KayttajatunnusLomake {
  etunimi: string
  sukunimi: string
  email: string
  password: string
}

@Component({
  templateUrl: './tervetuloa-uusi-kayttaja.component.html',
  styleUrls: ['./tervetuloa-uusi-kayttaja.component.css', '../login/login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TervetuloaUusiKayttajaComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  lomake = form(signal<KayttajatunnusLomake>({ etunimi: '', sukunimi: '', email: '', password: '' }), polku => {
    disabled(polku.etunimi)
    disabled(polku.sukunimi)
    disabled(polku.email)
    required(polku.password)
    validoiReaktiivisesti(polku.password, LemonaidValidators.passwordComplexityValidator)
  })
  year = new Date().getFullYear()
  readonly commonError = signal<string | null>(null)
  readonly showForm = signal(false)
  readonly isUsedLinkError = signal(false)
  hidePass: boolean = true

  private _luoTiliData: LuoTiliReq = {
    password: '',
    token: '',
  }
  private _userData: TervetuloaAnnaKayttajanTiedotResp
  private _luoTiliStarted: boolean

  constructor(
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _ladataanService: LadataanService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _validationService: FormValidationService,
    private _loginService: LoginService,
    private _kayttaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this._kayttaService.logout()
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this._route.paramMap
      .pipe(
        map(paramMap => paramMap.get('token')),
        switchMap(token => {
          if (token) {
            this._luoTiliData.token = token
            this._ladataanService.aloitaLataaminen()
            const requestData: AnnaKayttajanTiedotReq = {
              token: token,
            }
            return this._firebaseLemonaid.functionsCall<AnnaKayttajanTiedotReq, TervetuloaAnnaKayttajanTiedotResp>('tervetuloaAnnaKayttajaTiedot', requestData)
          }
          return of<TervetuloaAnnaKayttajanTiedotResp>(null)
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe({
        next: data => {
          if (data && !data.e) {
            this.showForm.set(true)
            this._userData = data
            asetaArvo(this.etunimi, this._userData.etunimi ?? '')
            asetaArvo(this.sukunimi, this._userData.sukunimi ?? '')
            asetaArvo(this.email, this._userData.email ?? '')
          } else if (data && [MagicLinkErrorResponse.USED, MagicLinkErrorResponse.EXPIRED].includes(data.e)) {
            this.isUsedLinkError.set(data.e === MagicLinkErrorResponse.USED)
            this.commonError.set('magic-link.' + data.e)
            this._errorHandler.handleError(data.e)
          } else {
            this.showForm.set(true)
            this.commonError.set('yleiset.tuntematon-virhe')
            this._errorHandler.handleError(new Error(data.e))
          }
          this._ladataanService.lopetaLataaminen()
        },
        error: error => {
          this.showForm.set(true)
          this.commonError.set('yleiset.tuntematon-virhe')
          this._errorHandler.handleError(error)
        },
      })
  }

  get etunimi() {
    return this.lomake.etunimi
  }
  get sukunimi() {
    return this.lomake.sukunimi
  }
  get email() {
    return this.lomake.email
  }
  get password() {
    return this.lomake.password
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }

  virheet(): { [laji: string]: boolean } {
    if (this.lomake) {
      if (this.password) {
        return Object.fromEntries(
          this.password()
            .errors()
            .map(virhe => [virhe.kind, true]),
        )
      }
    }
    return {}
  }

  routeToLogin() {
    this._router.navigate(['/kirjaudu'])
  }

  async luoTili() {
    if (this._luoTiliStarted) {
      return
    }
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._luoTiliStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const password: string = this.password().value()
      this._luoTiliData.password = password
      const response: LuoTiliResp = await this._firebaseLemonaid.functionsCall<LuoTiliReq, LuoTiliResp>('tervetuloaLuoTili', this._luoTiliData)

      this._ladataanService.lopetaLataaminen()

      if (response && !response.e) {
        const loginDataTransfer: LoginInputData = {
          email: this._userData.email,
          password: password,
          loginCodeId: response.loginCodeId,
        }
        this._loginService.changeInput(loginDataTransfer)
        this._router.navigate(['/lisaa-puhelin'])
      } else if (response && [MagicLinkErrorResponse.USED, MagicLinkErrorResponse.EXPIRED].includes(response.e)) {
        this.showForm.set(false)
        this.isUsedLinkError.set(response.e === MagicLinkErrorResponse.USED)
        this.commonError.set('magic-link.' + response.e)
        this._errorHandler.handleError(response.e)
      } else {
        this.commonError.set('yleiset.tuntematon-virhe')
        this._errorHandler.handleError(response ? response.e : 'no-response')
      }
      this._luoTiliStarted = false
    } catch (err) {
      this._ladataanService.lopetaLataaminen()
      this._luoTiliStarted = false
      this._errorHandler.handleError(err)
    }
  }
}
